import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { questionQualityApi } from '../../api/questionQualityApi';
import type { QualityVerdict } from '../../api/questionQualityApi';
import { findingsPath } from '../monitoring/questionQuality/verdictDisplay';
import './QuestionQualityBadge.css';

/**
 * Per-question quality verdicts, shown in the Questions detail panel. Read-only
 * view of the LLM-judged ledger (question-quality-checks); the fix loop is the
 * existing question edit path. See docs/FeatureDesigns/question-quality/checks-target.md.
 *
 * **"Not checked" is a state this states out loud.** It used to render nothing
 * at all when a question had no verdicts, which made never-checked, still-
 * loading and request-failed identical — so the one question this panel exists
 * to answer, "has this been looked at?", was the one it could not answer. The
 * flagged list cannot answer it either: it holds only questions WITH findings,
 * so absence there means "not flagged", never "not checked".
 */

const sevRank: Record<string, number> = { critical: 0, major: 1, minor: 2 };

export function QuestionQualityBadge({ question }: { question: unknown }) {
    const q = question as { id?: string; subject?: string };
    /**
     * Keyed by the question it describes, so a stale answer is never shown for a
     * new question — and so switching questions needs no state reset in an
     * effect. What is on screen is derived during render instead: a result for a
     * DIFFERENT id reads as "still loading", which is exactly what it is.
     */
    const [result, setResult] = useState<
        { id: string; verdicts: QualityVerdict[]; failed: boolean } | null
    >(null);

    useEffect(() => {
        const id = q.id;
        if (!id) return;
        let alive = true;
        questionQualityApi.byQuestion(id, q.subject)
            .then(r => { if (alive) setResult({ id, verdicts: r.verdicts, failed: false }); })
            // Distinguished from an empty ledger: "we could not ask" is not
            // "nobody has looked".
            .catch(() => { if (alive) setResult({ id, verdicts: [], failed: true }); });
        return () => { alive = false; };
    }, [q.id, q.subject]);

    const current = result && result.id === q.id ? result : null;

    if (!q.id) return null;
    if (!current) return <Shell>{'Checking…'}</Shell>;
    if (current.failed) return <Shell>{'Could not read the quality ledger.'}</Shell>;
    const verdicts = current.verdicts;
    if (verdicts.length === 0) {
        return (
            <Shell>
                Not checked yet — no rule has been run against this question.
            </Shell>
        );
    }

    const fails = verdicts.filter(v => v.verdict === 'fail').sort((a, b) => sevRank[a.severity] - sevRank[b.severity]);
    const unsure = verdicts.filter(v => v.verdict === 'unsure');
    const pass = verdicts.filter(v => v.verdict === 'pass').length;
    const lastChecked = verdicts.map(v => v.checkedAt).sort().slice(-1)[0];
    const listed = [...fails, ...unsure];

    return (
        <div className="qqb">
            <div className={`qqb-head${listed.length ? ' qqb-head--gap' : ''}`}>
                <b className="qqb-title">Quality checks</b>
                {fails.length > 0
                    ? <span className="qqb-pill qqb-pill--fail">{fails.length} fail{fails.length > 1 ? 's' : ''}</span>
                    : <span className="qqb-pill qqb-pill--clean">clean</span>}
                {unsure.length > 0 && <span className="qqb-unsure">{unsure.length} unsure</span>}
                <span className="qqb-pass">{pass} pass</span>
                {lastChecked && <span className="qqb-checked">checked {new Date(lastChecked).toLocaleDateString()}</span>}
                {/* Where the rationales are readable and a finding can be
                    resolved — this panel is the summary, not the workbench. */}
                {q.id && q.subject && (
                    <Link className="cs-link qqb-findings" to={findingsPath(q.id, q.subject)}>
                        Findings →
                    </Link>
                )}
            </div>
            {listed.map(v => (
                <div key={v.id} className="qqb-row">
                    <span className={`qqb-rule qqb-rule--${v.verdict === 'unsure' ? 'unsure' : v.severity}`}>
                        {v.ruleId}{v.verdict === 'unsure' ? ' (unsure)' : ` (${v.severity})`}
                    </span>
                    {': '}<span className="qqb-rationale">{v.rationale}</span>
                </div>
            ))}
        </div>
    );
}

/** One frame for every state, so "checked", "not checked" and "cannot tell"
 *  occupy the same place and are read the same way. */
function Shell({ children }: { children: React.ReactNode }) {
    return (
        <div className="qqb">
            <span className="qqb-title qqb-title--shell">Quality checks</span>
            <span className="qqb-shell-text">{children}</span>
        </div>
    );
}
